import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useImperativeHandle,
  memo,
} from "react";
import { Container, Graphics } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../../Contexts/DimensionsContext";

import { POPUP_OVERLAY_CONFIG } from "../../../../pixiSettings";
import { SOUND_NAME } from "../../../../sounds";
import anime from "animejs/lib/anime.es.js";
import FreeSpinIntroDialogController from "./FreeSpinIntroDialogController";
import { safeInit } from "../../../../utilities";
import {
  selectShowFreeSpinIntro,
  selectFreeSpinsAwarded,
  selectFreeSpinsPackageTier,
  selectFreeSpinsRemaining,
  selectShowFreeSpinsReload,
} from "../../../../State/selectors";
import { soundManager } from "../../../../SoundManager";

// The one shared backdrop, so this popup darkens the game to exactly the same
// depth as every other. It used to carry its own 0x090f41 / 0.85 / 350.
const OVERLAY_BACKGROUND_COLOR = POPUP_OVERLAY_CONFIG.COLOR;
const OVERLAY_BACKGROUND_ALPHA = POPUP_OVERLAY_CONFIG.ALPHA;
const OVERLAY_FADE_DURATION = POPUP_OVERLAY_CONFIG.FADE_DURATION;

/**
 * @param {Object} p
 * @param {"intro"|"reload"} [p.variant]
 *   "intro"  the package was just won — CONGRATULATIONS / YOU WON / n / FREE
 *            SPINS, and closing it wipes into the room.
 *   "reload" a reload landed mid-round — FREE SPINS / n / LEFT, n being the
 *            spins REMAINING, and closing it just hands the UI back because the
 *            room is already restored behind it.
 *
 * Both are the same spine, the same controller and the same sequence; only the
 * copy, the number and the moment differ. Mounted twice from Dialogs.jsx rather
 * than duplicated — see the controller's own note on why.
 */
const FreeSpinIntroDialog = ({ variant = "intro" }) => {
  const isReload = variant === "reload";
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const showIntro = useSelector(selectShowFreeSpinIntro);
  const showReload = useSelector(selectShowFreeSpinsReload);
  const showFreeSpinIntro = isReload ? showReload : showIntro;
  // PLURAL — the Mad Labs slice. This used to read featureGame.freeSpinCount,
  // Zeus's singular field, which nothing in this game ever sets: the popup
  // always announced 0.
  const awarded = useSelector(selectFreeSpinsAwarded);
  // The reload popup counts what is LEFT, not what was won.
  const remaining = useSelector(selectFreeSpinsRemaining);
  const freeSpinCount = isReload ? remaining : awarded;
  // The package the ladder SETTLED on, so a gambled-up round shows the tier
  // the player actually holds rather than the one the scatter first revealed.
  const packageTier = useSelector(selectFreeSpinsPackageTier);

  const introControllerRef = useRef(null);
  const introContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const dimensionsContext = useDimensions();
  const {
    dimensions,
    uiSymbolHeight: uiSymbolSize,
    device,
  } = dimensionsContext;


  // Overlay state (matching WinAnimation pattern)
  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const overlayAnimRef = useRef(null);

  // Expose fadeInBackground / fadeOutBackground via overlayRef (same API as WinAnimation)
  useImperativeHandle(overlayRef, () => ({
    fadeInBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          if (callback && typeof callback === "function") callback();
        },
      });
    },
    fadeOutBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: OVERLAY_BACKGROUND_ALPHA },
        alpha: 0,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          setOverlayAlpha(0);
          if (callback && typeof callback === "function") callback();
        },
      });
    },
    setAlpha(alpha) {
      setOverlayAlpha(alpha);
    },
  }));

  // Initialize controller once
  useEffect(() => {
    if (introContainerRef.current && !introControllerRef.current) {
      safeInit(() => {
        introControllerRef.current = new FreeSpinIntroDialogController(
          introContainerRef.current,
          dimensionsContext,
          dispatch,
          uiSymbolSize,
          overlayRef,
          t,
          variant,
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle dimension changes
  useEffect(() => {
    if (introControllerRef.current) {
      introControllerRef.current.updateDimensions(
        dimensionsContext,
        uiSymbolSize,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions.width, dimensions.height, uiSymbolSize, device]);

  // Play animation when triggered
  useEffect(() => {
    if (introControllerRef.current && showFreeSpinIntro) {
      // Tier FIRST. setTier swaps the level skin and rebuilds the count text,
      // because each level writes into its own value slot — setting the count
      // before the skin would write it into the slot the old level owned.
      introControllerRef.current.setTier(packageTier);
      introControllerRef.current.updateFreeSpinCount(freeSpinCount);
      introControllerRef.current.startSequence();
    }
  }, [showFreeSpinIntro, freeSpinCount, packageTier]);



  // Handle user click on overlay to close animation
  const handleBackgroundClick = useCallback(
    (evt) => {
      if (evt && typeof evt.preventDefault === "function") evt.preventDefault();
      if (evt && typeof evt.stopPropagation === "function")
        evt.stopPropagation();
      if (evt && typeof evt.stopImmediatePropagation === "function")
        evt.stopImmediatePropagation();

      if (introControllerRef.current && showFreeSpinIntro) {
        const accepted = introControllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.tap_to_continue);
        }
      }
    },
    [showFreeSpinIntro],
  );

  // Add spacebar handling
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      // Ignore auto-repeat
      if (event.repeat) {
        event.preventDefault();
        if (typeof event.stopPropagation === "function")
          event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function")
          event.stopImmediatePropagation();
        return;
      }

      if (!showFreeSpinIntro || !introControllerRef.current) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      try {
        const accepted = introControllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.tap_to_continue);
        }
      } catch (error) {
        if (
          introControllerRef.current &&
          introControllerRef.current.finishAnimation
        ) {
          introControllerRef.current.finishAnimation();
        }
      }
    };

    if (showFreeSpinIntro) {
      document.addEventListener("keydown", handleKeyDown, {
        capture: true,
        passive: false,
      });
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [showFreeSpinIntro, dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (introControllerRef.current) {
        introControllerRef.current.cleanup();
      }
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
    };
  }, []);

  // Memoize container position
  const containerPosition = useMemo(
    () => ({
      x: dimensions.width / 2,
      y: dimensions.height / 2,
    }),
    [dimensions.width, dimensions.height],
  );

  // Overlay draw function (matching WinAnimation)
  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(OVERLAY_BACKGROUND_COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  return (
    <Container
      name="free_spin_intro"
      x={containerPosition.x}
      y={containerPosition.y}
      sortableChildren={true}
      zIndex={9}
      ref={introContainerRef}
      // Gates the WHOLE subtree. The tap-to-continue container is eventMode
      // "static" with its own hit area, and closing only drops the spine's
      // alpha — so without this it stays invisible but still swallowing
      // pointer events, for every popup, for ever. Every Zeus popup carries
      // this guard; these five were missing it.
      eventMode={showFreeSpinIntro ? "auto" : "none"}
    >
      {/* Background overlay - inline Graphics (WinAnimation pattern) */}
      <Graphics
        name="free_spin_intro_overlay"
        draw={drawOverlay}
        alpha={overlayAlpha}
        x={-containerPosition.x}
        y={-containerPosition.y}
        zIndex={0}
        eventMode={showFreeSpinIntro ? "static" : "none"}
        pointerdown={handleBackgroundClick}
        // Clickable, but NEVER a pointer. Every Zeus popup pins this to
        // "default" — only the tap-to-continue word offers a pointer.
        cursor="default"
      />

      {/* Spine, texts and the tap-to-continue button are all owned by
          FreeSpinIntroDialogController and pinned into the spine's own slots. */}
    </Container>
  );
};

export default memo(FreeSpinIntroDialog);
